/**
 * Derived ViewState from RoundSnapshot.
 *
 * Canonical phase sequence: WAITING → BETTING → SPINNING → TRICKS → SLOWING → RESOLVED
 * Phase = backend status (full sync with backend naming).
 *
 * Timers (from backend, updated per snapshot):
 * - BETTING: betting_ends_at
 * - SPINNING: spinning_ends_at
 * - TRICKS: tricks_ends_at (or inferred from tricks_duration + spin/tricks start when ends_at missing)
 * - SLOWING: slowing_ends_at
 *
 * TRICKS UI (countdown + trick buttons) also treats SPINNING after `spinning_ends_at` as active tricks
 * window — same edge as `useLivePvpRoomPhase` — so UI does not wait for a late `status: TRICKS` push.
 */

import {
  centidegreesToDegrees,
  getEffectiveTargetCoordinate,
  getActiveBets,
  getEffectiveTiming,
  normalizeUnixTimestampToMs,
} from "@/shared/api/types/pvpRoulette"
import { DEFAULT_GROUP_ID } from "@/shared/constants/pvpRoulette"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import type {
  RoundSnapshot,
  DerivedViewState,
  ViewPhase,
  BetRestrictions,
  BanksState,
} from "./types"

export interface DeriveContext {
  now: number
  userId?: string
  bankSource?: "parties" | "bets"
  /**
   * Clock-drift offset (ms): server_time − Date.now() from the latest push.
   * Applied only to UI countdown calculations; not to animation/status logic.
   */
  clockOffsetMs?: number
}

function toMs(ts: number | null | undefined): number {
  if (ts == null || !Number.isFinite(ts)) return 0
  return normalizeUnixTimestampToMs(ts)
}

function derivePhase(snapshot: RoundSnapshot, _now: number): ViewPhase {
  const { status } = snapshot
  if (status === "RESOLVED") return "RESOLVED"
  if (status === "SLOWING") return "SLOWING"
  if (status === "TRICKS") return "TRICKS"
  if (status === "SPINNING") return "SPINNING"
  if (status === "BETTING") return "BETTING"
  if (status === "WAITING") return "WAITING"

  return "WAITING"
}

function distinctPartiesCount(snapshot: RoundSnapshot): number {
  const active = getActiveBets(snapshot.bets)
  const ids = new Set(active.map((b) => b.partyId ?? b.userData.userId))
  return Math.max(ids.size, snapshot.parties.length)
}

/** Countdown from betting_ends_at. Only in BETTING status (backend may extend per new bet). */
function computeRemainingMsBetting(
  snapshot: RoundSnapshot,
  now: number
): number | null {
  if (snapshot.status !== "BETTING") return null
  const t = getEffectiveTiming(snapshot)
  const end = t.bettingEndsAt != null ? toMs(t.bettingEndsAt) : null
  if (!end) return null
  const remaining = end - now
  return remaining > 0 ? remaining : 0
}

/** Countdown from spinning_ends_at. commit_hash available from SPINNING. */
function computeRemainingMsSpinning(
  snapshot: RoundSnapshot,
  now: number
): number | null {
  const t = getEffectiveTiming(snapshot)
  const end = t.spinningEndsAt != null ? toMs(t.spinningEndsAt) : null
  if (!end) return null
  if (snapshot.status !== "SPINNING") return null
  const remaining = end - now
  return remaining > 0 ? remaining : 0
}

/**
 * Same idea as `useLivePvpRoomPhase`: after `spinning_ends_at` the client may already be in TRICKS
 * while the last snapshot still says SPINNING until Centrifugo catches up. Treat that gap as TRICKS
 * for countdown + trick buttons so UI matches local clock (not WS latency).
 */
function isTricksUiActive(snapshot: RoundSnapshot, now: number): boolean {
  if (snapshot.status === "TRICKS") return true
  if (snapshot.status !== "SPINNING") return false
  const t = getEffectiveTiming(snapshot)
  if (t.spinningEndsAt == null || !Number.isFinite(t.spinningEndsAt))
    return false
  return now >= normalizeUnixTimestampToMs(t.spinningEndsAt)
}

/** Raw tricks_ends_at when present; else `tricks_started_at|spinning_ends_at + tricksDurationSeconds`. */
function inferRawTricksEndsAtMs(snapshot: RoundSnapshot): number | null {
  const t = getEffectiveTiming(snapshot)
  if (t.tricksEndsAt != null && Number.isFinite(t.tricksEndsAt)) {
    return normalizeUnixTimestampToMs(t.tricksEndsAt)
  }
  const sec = snapshot.tricksDurationSeconds
  if (sec == null || sec <= 0) return null
  const start =
    t.tricksStartedAt != null && Number.isFinite(t.tricksStartedAt)
      ? normalizeUnixTimestampToMs(t.tricksStartedAt)
      : t.spinningEndsAt != null && Number.isFinite(t.spinningEndsAt)
        ? normalizeUnixTimestampToMs(t.spinningEndsAt)
        : null
  if (start == null) return null
  return start + sec * 1000
}

/** Visual deadline for trick UI (matches `TRICKS_VISUAL_END_LEAD_MS` in TricksPanel). */
function getVisualTricksEndsAtMsForUi(
  snapshot: RoundSnapshot,
  now: number
): number | null {
  if (!isTricksUiActive(snapshot, now)) return null
  const raw = inferRawTricksEndsAtMs(snapshot)
  if (raw == null) return null
  return raw - getPvpRouletteRuntime().TRICKS_VISUAL_END_LEAD_MS
}

/** Countdown to visual tricks end while TRICKS (or optimistic TRICKS gap after spin). */
function computeRemainingMsTricks(
  snapshot: RoundSnapshot,
  now: number
): number | null {
  const end = getVisualTricksEndsAtMsForUi(snapshot, now)
  if (end == null) return null
  const remaining = end - now
  return remaining > 0 ? remaining : 0
}

/** Countdown from slowing_ends_at. Only in SLOWING. */
function computeRemainingMsSlowing(
  snapshot: RoundSnapshot,
  now: number
): number | null {
  const t = getEffectiveTiming(snapshot)
  const end = t.slowingEndsAt != null ? toMs(t.slowingEndsAt) : null
  if (!end) return null
  if (snapshot.status !== "SLOWING") return null
  const remaining = end - now
  return remaining > 0 ? remaining : 0
}

function endsAtMs(
  snapshot: RoundSnapshot,
  now: number
): {
  betting: number | null
  spinning: number | null
  tricks: number | null
  slowing: number | null
} {
  const t = getEffectiveTiming(snapshot)
  return {
    betting: t.bettingEndsAt != null ? toMs(t.bettingEndsAt) : null,
    spinning: t.spinningEndsAt != null ? toMs(t.spinningEndsAt) : null,
    tricks: getVisualTricksEndsAtMsForUi(snapshot, now),
    slowing: t.slowingEndsAt != null ? toMs(t.slowingEndsAt) : null,
  }
}

/** Текущий пользователь в скваде (TEAM / default group) — трики недоступны. */
function userParticipatesInSquad(
  snapshot: RoundSnapshot,
  userId?: string
): boolean {
  if (!userId) return false
  const active = getActiveBets(snapshot.bets)
  const bet = active.find((b) => b.userData.userId === userId)
  if (!bet) return false
  return (
    bet.partyType === "TEAM" ||
    bet.partyId === DEFAULT_GROUP_ID ||
    bet.groupId === DEFAULT_GROUP_ID
  )
}

function deriveTricksActionsWhenWindowOpen(
  snapshot: RoundSnapshot,
  now: number,
  userId?: string
): {
  canPlaceBet: false
  canUseTrick: boolean
  canApplyTrickByType: Record<string, boolean>
} | null {
  const tricksWindowOpen = (computeRemainingMsTricks(snapshot, now) ?? 0) > 0
  if (!tricksWindowOpen) return null
  const allowed = snapshot.allowedTricks ?? []
  const userUsedAnyTrick =
    userId != null && snapshot.tricks.some((t) => t.userId === userId)
  const squadBlocksTricks = userParticipatesInSquad(snapshot, userId)
  const canApplyTrickByType: Record<string, boolean> = {}
  for (const trickType of allowed) {
    if (!trickType) continue
    const usedThisType =
      userId != null &&
      snapshot.tricks.some((t) => t.userId === userId && t.type === trickType)
    canApplyTrickByType[trickType] =
      !squadBlocksTricks && !userUsedAnyTrick && !usedThisType
  }
  const canUseTrick = Object.values(canApplyTrickByType).some(Boolean)
  return { canPlaceBet: false, canUseTrick, canApplyTrickByType }
}

/** Bets in WAITING/BETTING. Tricks in TRICKS and in SPINNING after spin end (pre-WS TRICKS snapshot). */
function deriveActionsAllowed(
  snapshot: RoundSnapshot,
  phase: ViewPhase,
  now: number,
  userId?: string
): {
  canPlaceBet: boolean
  canUseTrick: boolean
  canApplyTrickByType: Record<string, boolean>
} {
  if (phase === "RESOLVED") {
    return { canPlaceBet: false, canUseTrick: false, canApplyTrickByType: {} }
  }
  if (phase === "WAITING" || phase === "BETTING") {
    return { canPlaceBet: true, canUseTrick: false, canApplyTrickByType: {} }
  }
  if (phase === "SPINNING") {
    const tricks = deriveTricksActionsWhenWindowOpen(snapshot, now, userId)
    if (tricks != null) return tricks
    return { canPlaceBet: false, canUseTrick: false, canApplyTrickByType: {} }
  }
  if (phase === "SLOWING") {
    return { canPlaceBet: false, canUseTrick: false, canApplyTrickByType: {} }
  }
  if (phase === "TRICKS") {
    const tricks = deriveTricksActionsWhenWindowOpen(snapshot, now, userId)
    if (tricks == null)
      return { canPlaceBet: false, canUseTrick: false, canApplyTrickByType: {} }
    return tricks
  }
  return { canPlaceBet: false, canUseTrick: false, canApplyTrickByType: {} }
}

function deriveBetRestrictions(
  snapshot: RoundSnapshot,
  userId?: string
): BetRestrictions {
  const sectorIds = new Set<string>()
  const active = getActiveBets(snapshot.bets)
  snapshot.parties.forEach((p) => {
    if (p.sectorId) sectorIds.add(p.sectorId)
    if (typeof p.sectorFrom === "number" && typeof p.sectorTo === "number") {
      sectorIds.add(`${p.sectorFrom}:${p.sectorTo}`)
    }
  })
  active.forEach((b) => {
    if (b.sectorId) sectorIds.add(b.sectorId)
  })
  const userHasBet = userId
    ? active.some((b) => b.userData.userId === userId)
    : false
  return { occupiedSectorIds: Array.from(sectorIds), userHasBet }
}

function deriveBanks(snapshot: RoundSnapshot): BanksState {
  const prizePoolTon = snapshot.prizePoolTon ?? "0"
  const hasFromSnapshot = prizePoolTon !== "" && parseFloat(prizePoolTon) > 0
  const active = getActiveBets(snapshot.bets)
  const fromBets = active
    .reduce((sum, b) => sum + parseFloat(b.amount || "0"), 0)
    .toFixed(2)
  return {
    prizePoolTon: hasFromSnapshot ? prizePoolTon : fromBets,
    fallbackFromBets: !hasFromSnapshot,
  }
}

export function deriveViewState(
  snapshot: RoundSnapshot,
  ctx: DeriveContext
): DerivedViewState {
  const phase = derivePhase(snapshot, ctx.now)
  const uiNow =
    ctx.clockOffsetMs != null && Number.isFinite(ctx.clockOffsetMs)
      ? ctx.now + ctx.clockOffsetMs
      : ctx.now
  const targetCoord = getEffectiveTargetCoordinate(snapshot)
  const targetDegrees = centidegreesToDegrees(targetCoord)
  const currentDegrees =
    snapshot.currentCoordinate != null
      ? centidegreesToDegrees(snapshot.currentCoordinate)
      : targetDegrees
  const ends = endsAtMs(snapshot, uiNow)

  if (
    import.meta.env.DEV &&
    ctx.clockOffsetMs != null &&
    Math.abs(ctx.clockOffsetMs) > 5000
  ) {
    console.warn(
      `[deriveViewState] Large clock offset detected: ${ctx.clockOffsetMs}ms`
    )
  }

  return {
    phase,
    status: snapshot.status,
    roundId: snapshot.roundId,
    roundNum: snapshot.roundNum,
    remainingMsBetting: computeRemainingMsBetting(snapshot, uiNow),
    remainingMsSpinning: computeRemainingMsSpinning(snapshot, uiNow),
    remainingMsTricks: computeRemainingMsTricks(snapshot, uiNow),
    remainingMsSlowing: computeRemainingMsSlowing(snapshot, uiNow),
    endsAtBettingMs: ends.betting,
    endsAtSpinningMs: ends.spinning,
    endsAtTricksMs: ends.tricks,
    endsAtSlowingMs: ends.slowing,
    actionsAllowed: deriveActionsAllowed(snapshot, phase, uiNow, ctx.userId),
    betRestrictions: deriveBetRestrictions(snapshot, ctx.userId),
    banks: deriveBanks(snapshot),
    parties: snapshot.parties,
    targetDegrees,
    currentDegrees,
    commitHash: snapshot.commitHash,
    salt: snapshot.salt,
    roulettePosition: snapshot.roulettePosition,
  }
}
